/**
 * REDFOX PR3-C-2: 社交传播风险（weekly_social_risk 聚合）前端类型 + 渲染 helper。
 *
 * 消费 PR3-C-1 social-risk 读 API（端点见 SOCIAL_RISK_ENDPOINT）。**独立于** config-center 的
 * `SocialDataSource`/`SocialRun`（那是 social_api 源观测语义，≠ 本 brand-week 聚合）。
 * honest 红线：insufficient/no_run → 「—」非 0/绿；0 绑 raw 字段；0 进 VOC/NPS。
 */

// M1：shared `api` client baseURL='/api/v2' → path 不带前缀（禁双前缀 /api/v2/api/v2/...）
export const SOCIAL_RISK_ENDPOINT = '/social-risk/weekly';

export type SocialSignalStatus = 'ok' | 'partial' | 'insufficient' | 'no_run';

export interface SocialPlatformBreakdown {
  stored_post_count: number;
  after_dedupe: number;
  vol_norm: number;
  eng_norm: number;
  platform_signal: number;
  outlier_count: number;
}

/** brand-week 行（照 PR3-C-1 `_row_public` 14 字段；不含逐帖 raw count）。 */
export interface WeeklySocialRiskRow {
  week: string;
  brand_id: string;
  stored_post_count: number; // mapped stored post 数（≠ run-level after_dedupe / ≠ 全网全量）
  after_dedupe_count: number;
  platform_count: number;
  query_count: number;
  social_propagation_index: number | null; // [0,100]；insufficient/no_run → null
  high_engagement_outlier_count: number; // 单列，不进主分
  signal_status: SocialSignalStatus;
  signal_reasons: string[]; // normalized SOCIAL_*
  platform_breakdown: Record<string, SocialPlatformBreakdown>;
  collection_run_ids: number[];
  formula_version: string;
  sentiment_status: string; // 恒 nlp_unverified
}

export interface WeeklySocialRiskResponse {
  week: string;
  disclosure: string;
  rows: WeeklySocialRiskRow[];
}

// M3：API 只返 brand_id（非 brand_name）。主标用 brand_id；前端可选 brand-label 富化。
export const BRAND_LABEL: Record<string, string> = {
  'GAC-AION': '广汽埃安',
  'GAC-HYPER': '广汽昊铂',
  'GAC-HONDA': '广汽本田',
  'GAC-TOYOTA': '广汽丰田',
  'GAC-TRUMP': '广汽传祺',
  'GAC-QIJING': '广汽启境',
};

export function brandLabel(brandId: string): string {
  return BRAND_LABEL[brandId] ?? brandId; // 无富化 → 主标 brand_id
}

export function isInsufficientState(s: SocialSignalStatus): boolean {
  return s === 'insufficient' || s === 'no_run';
}

/** index 显示：ok/partial → 数值（1 位小数）；insufficient/no_run(null) → 「—」（非 0/绿）。 */
export function socialIndexDisplay(row: Pick<WeeklySocialRiskRow, 'social_propagation_index'>): string {
  return row.social_propagation_index == null ? '—' : row.social_propagation_index.toFixed(1);
}

// 状态 → 独立 social 色阶（**非** VOC/NPS 风险色码；ok 用 blue 而非绿，避「低风险绿」混同）。
export const SOCIAL_STATUS_COLOR: Record<SocialSignalStatus, string> = {
  ok: 'blue',
  partial: 'orange',
  insufficient: 'default',
  no_run: 'default',
};

export const SOCIAL_STATUS_LABEL: Record<SocialSignalStatus, string> = {
  ok: '社交传播信号',
  partial: '有限/部分数据',
  insufficient: '信号不足',
  no_run: '暂无采集',
};

// 响应级空态（aggregate-unavailable，B1）：区别于 row 级 no_run/insufficient
export const SOCIAL_RISK_EMPTY_TEXT = '本周暂无社交传播聚合 / 等待 weekly_social_risk recompute';
